import { useEffect, useRef } from 'react';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import delay from '@zenbusiness/application-commons-utils/delay';
import { styled, useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';

import { FundboxContainerStyle } from './styles';

const FundboxContainer = styled('div')(FundboxContainerStyle);

export default function FundboxFuseWidget({ partnerId, email, phoneNumber, partnerUserId }) {
  const trackFundBoxDisplayed = useClickstreamCallback('bannerDisplayed');
  const fundboxContainerRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const fuseButtonDiv = document.createElement('div');
    fuseButtonDiv.id = 'fundbox-fuse-button';
    fuseButtonDiv.setAttribute('data-fundbox-domain', 'app.fundbox.com');
    fuseButtonDiv.setAttribute('data-fundbox-id', partnerId);
    fuseButtonDiv.setAttribute('data-fundbox-email', email);
    fuseButtonDiv.setAttribute('data-fundbox-partner-user-id', partnerUserId);
    fuseButtonDiv.setAttribute('data-fundbox-phone-number', phoneNumber);

    // Append the div to the component's container
    const container = document.getElementById('fundbox-container');
    if (container) {
      container.appendChild(fuseButtonDiv);
    } else {
      console.error('Container for Fundbox Fuse Button not found.');
      return;
    }

    // Function to dynamically load the Fuse script
    const loadFuseScript = () => {
      const scriptId = 'fuse-script';
      // Check if the script is already present
      if (document.getElementById(scriptId)) {
        console.warn('Fuse script already loaded.');
        return;
      }

      // Set window.FbxFuse to null before adding the script tag
      window.FbxFuse = null;

      const existingIframe = document.getElementById('fundbox-fuse-app');
      if (existingIframe) {
        existingIframe.parentNode.removeChild(existingIframe);
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'text/javascript';
      script.src = 'https://app.fundbox.com/static/dist/fuse/v1/fuse.js';
      script.onerror = () => {
        throw new URIError(`The script ${script.src} is not accessible.`);
      };
      script.onload = () => {
        delay(() => {
          trackFundBoxDisplayed({
            bannerType: 'info',
            elementName: 'fundbox_banner',
            appPageName: 'active_banking_dashboard'
          });
          if (fundboxContainerRef.current) {
            Object.assign(fundboxContainerRef.current.style, {
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: '16px',
              overflow: 'hidden'
            });
          }
        }, 2000);
      };

      // Insert the script before the current script
      const { currentScript } = document;
      if (currentScript && currentScript.parentNode) {
        currentScript.parentNode.insertBefore(script, currentScript);
      } else {
        // Fallback: append to head
        document.head.appendChild(script);
      }
    };

    // Load the Fuse script after the div is in place
    loadFuseScript();

    // Cleanup function to remove event listeners and script
    // eslint-disable-next-line consistent-return
    return () => {
      const existingScript = document.getElementById('fuse-script');
      if (existingScript) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, email, phoneNumber, partnerUserId, theme.palette.border.main]);

  return (
    <FundboxContainer
      id="fundbox-container"
      ref={fundboxContainerRef}
    />
  );
}

FundboxFuseWidget.propTypes = {
  partnerId: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  partnerUserId: PropTypes.string.isRequired
};
